// Sprint 17 A4: Unified Chronological Timeline — all sources interleaved
import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
  Mail, Smartphone, MessageCircle, Phone, Calendar,
  Handshake, FileText, StickyNote, Settings, Globe,
  ArrowDownLeft, ArrowUpRight, Brain, Sparkles,
} from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useCrmTimeline, useCrmSummarize } from "@/hooks/use-crm-timeline";
import { formatMonthDayTime } from "@/lib/i18n";
import type { TimelineEntry, TimelineEntryType } from "@/hooks/use-crm-timeline";

interface UnifiedTimelineProps {
  contactId: string;
}

const TYPE_CONFIG: Record<TimelineEntryType, { Icon: typeof Mail; color: string; label: string }> = {
  email: { Icon: Mail, color: "text-[var(--color-primary)]", label: "Email" },
  sms: { Icon: Smartphone, color: "text-[var(--color-success)]", label: "SMS" },
  whatsapp: { Icon: MessageCircle, color: "text-[var(--color-success)]", label: "WhatsApp" },
  instagram: { Icon: MessageCircle, color: "text-[var(--color-warning)]", label: "Instagram" },
  messenger: { Icon: MessageCircle, color: "text-[var(--color-info)]", label: "Messenger" },
  call: { Icon: Phone, color: "text-[var(--color-ai)]", label: "Call" },
  meeting: { Icon: Calendar, color: "text-[var(--color-accent-action)]", label: "Meeting" },
  booking: { Icon: Calendar, color: "text-[var(--color-accent-action)]", label: "Booking" },
  deal: { Icon: Handshake, color: "text-[var(--color-warning)]", label: "Deal" },
  form: { Icon: FileText, color: "text-[var(--color-info)]", label: "Form" },
  page: { Icon: Globe, color: "text-[var(--color-primary)]", label: "Page" },
  note: { Icon: StickyNote, color: "text-[var(--color-muted-foreground)]", label: "Note" },
  task: { Icon: FileText, color: "text-[var(--color-info)]", label: "Task" },
  webinar: { Icon: Calendar, color: "text-[var(--color-ai)]", label: "Webinar" },
  ai_summary: { Icon: Brain, color: "text-[var(--color-ai)]", label: "AI Summary" },
  system: { Icon: Settings, color: "text-[var(--color-muted-foreground)]", label: "System" },
};

const FILTER_TYPES: TimelineEntryType[] = ["email", "sms", "whatsapp", "instagram", "messenger", "call", "meeting", "booking", "deal", "form", "page", "note", "task", "webinar"];

export function UnifiedTimeline({ contactId }: UnifiedTimelineProps) {
  const { data: entries, isLoading } = useCrmTimeline(contactId);
  const { summarize, isLoading: isSummarizing } = useCrmSummarize(contactId);
  const [filter, setFilter] = useState<TimelineEntryType | "all">("all");
  const [generatedSummary, setGeneratedSummary] = useState<TimelineEntry | null>(null);

  useEffect(() => {
    setGeneratedSummary(null);
  }, [contactId]);

  const filtered = filter === "all"
    ? (entries ?? [])
    : (entries ?? []).filter((e) => e.type === filter);

  // Sort by timestamp descending (most recent first)
  const sorted = [...filtered].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  // Separate AI summary from timeline
  const aiSummary = [generatedSummary, ...(entries ?? []).filter((e) => e.type === "ai_summary")]
    .filter((entry): entry is TimelineEntry => Boolean(entry))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
  const timelineEntries = sorted.filter((e) => e.type !== "ai_summary");

  const handleSummarize = async () => {
    const result = await summarize();
    if (!result?.summary) return;

    setGeneratedSummary({
      id: `generated-ai-summary-${contactId}-${Date.now()}`,
      type: "ai_summary",
      title: "AI contact summary",
      body: result.summary,
      timestamp: new Date().toISOString(),
      isAiGenerated: result.aiGenerated,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* AI Summary card */}
      {aiSummary && (
        <div className="mx-4 mt-4 rounded-lg border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] p-3">
          <div className="flex items-center gap-2 mb-1">
            <Brain className="h-4 w-4 text-[var(--color-ai)]" />
            <span className="text-xs font-semibold text-[var(--color-ai)]">AI Summary</span>
            <span className="text-[9px] px-1 py-0.5 rounded bg-[var(--color-ai)]/10 text-[var(--color-ai)] border border-[var(--color-ai-border)]">
              {aiSummary.isAiGenerated === false ? "Fallback Summary" : "AI Generated"}
            </span>
          </div>
          <p className="text-xs text-[var(--color-foreground)] leading-relaxed">{aiSummary.body}</p>
        </div>
      )}
      {!aiSummary && (
        <div className="mx-4 mt-4">
          <button
            onClick={handleSummarize}
            disabled={isSummarizing}
            className="inline-flex items-center gap-1.5 text-xs text-[var(--color-ai)] hover:text-[var(--color-ai)]/80 disabled:opacity-50"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {isSummarizing ? "Generating summary..." : "Generate AI summary"}
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-[var(--color-border)] overflow-x-auto">
        <button
          onClick={() => setFilter("all")}
          className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap transition-colors ${
            filter === "all"
              ? "bg-[var(--color-primary-light)] text-[var(--color-primary)]"
              : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)]"
          }`}
        >
          All
        </button>
        {FILTER_TYPES.map((t) => {
          const config = TYPE_CONFIG[t];
          const Icon = config.Icon;
          return (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full whitespace-nowrap transition-colors ${
                filter === t
                  ? "bg-[var(--color-primary-light)] text-[var(--color-primary)]"
                  : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)]"
              }`}
            >
              <Icon className="h-3 w-3" /> {config.label}
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <TimelineSkeleton />
        ) : timelineEntries.length === 0 ? (
          <div className="text-center text-[var(--color-muted-foreground)] py-12">
            <p className="text-sm">No activity yet</p>
            <p className="text-xs mt-1">Events will appear here as they happen</p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-[var(--color-border)]" />

            {timelineEntries.map((entry) => (
              <TimelineCard key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineCard({ entry }: { entry: TimelineEntry }) {
  const config = TYPE_CONFIG[entry.type] ?? TYPE_CONFIG.note;
  const Icon = config.Icon;
  const [isExpanded, setIsExpanded] = useState(false);
  const hasDetails = Boolean(entry.details?.length || entry.metadata);

  return (
    <div className="stagger-item relative pl-10 pb-4">
      {/* Dot on timeline */}
      <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-[var(--color-card)] bg-[var(--color-border)] ${config.color}`}>
        <Icon className="h-3 w-3 absolute -top-0.5 -left-0.5 hidden" />
      </div>

      <div className="border border-[var(--color-border)] rounded-lg px-3 py-2 bg-[var(--color-card)] hover:border-[var(--color-primary-border)] transition-colors">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${config.color}`} />
            <span className="text-sm font-medium text-[var(--color-foreground)] truncate">{entry.title}</span>
            {entry.direction && (
              <span className={`inline-flex items-center gap-0.5 text-[10px] px-1 rounded ${
                entry.direction === "inbound"
                  ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
                  : "bg-[var(--color-primary-light)] text-[var(--color-primary)]"
              }`}>
                {entry.direction === "inbound"
                  ? <><ArrowDownLeft className="h-2 w-2" /> in</>
                  : <><ArrowUpRight className="h-2 w-2" /> out</>}
              </span>
            )}
            {entry.isAiGenerated && (
              <span className="text-[10px] px-1 rounded bg-[var(--color-ai-light)] text-[var(--color-ai)] border border-[var(--color-ai-border)]">
                AI
              </span>
            )}
          </div>
          <span className="text-xs text-[var(--color-muted-foreground)] flex-shrink-0">
            {formatMonthDayTime(entry.timestamp)}
          </span>
        </div>
        {entry.body && (
          <p className={`text-xs text-[var(--color-muted-foreground)] mt-1 ${isExpanded ? "" : "line-clamp-2"}`}>{entry.body}</p>
        )}
        {entry.actorName && (
          <p className="text-[10px] text-[var(--color-muted-foreground)] mt-1">by {entry.actorName}</p>
        )}
        {hasDetails && (
          <button
            type="button"
            onClick={() => setIsExpanded((current) => !current)}
            className="mt-2 text-xs text-[var(--color-primary)] hover:underline"
          >
            {isExpanded ? "Hide details" : "Show details"}
          </button>
        )}
        {isExpanded && entry.details && entry.details.length > 0 && (
          <dl className="mt-3 grid gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-3">
            {entry.details.map((detail) => (
              <div key={`${entry.id}-${detail.label}`} className="grid gap-1 sm:grid-cols-[120px_1fr] sm:items-start">
                <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                  {detail.label}
                </dt>
                <dd className="text-xs text-[var(--color-foreground)] break-words">
                  {detail.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {isExpanded && !entry.details?.length && entry.metadata && (
          <pre className="mt-3 overflow-x-auto rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-3 text-[11px] text-[var(--color-muted-foreground)]">
            {JSON.stringify(entry.metadata, null, 2)}
          </pre>
        )}
        {entry.actionable && (
          <Link to={entry.actionable.href} className="text-xs text-[var(--color-primary)] hover:underline mt-1 block">
            {entry.actionable.label} →
          </Link>
        )}
      </div>
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="relative">
      <div className="absolute left-4 top-0 bottom-0 w-px bg-[var(--color-border)]" />
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="relative pl-10 pb-4">
          <Skeleton className="absolute left-2.5 h-3 w-3 rounded-full" />
          <SkeletonCard className="space-y-2" />
        </div>
      ))}
    </div>
  );
}
